import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { TIME_BLOCKS } from '../../utils/todoConstants';
import './ComplianceTab.css';

export default function ComplianceTab({ clientId, client }) {
  const [items, setItems] = useState([]);
  const [checkoffs, setCheckoffs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [clientId]);

  async function loadData() {
    setLoading(true);

    const [itemsRes, checkoffsRes] = await Promise.all([
      supabase
        .from('todo_items')
        .select('*')
        .eq('client_id', clientId)
        .eq('is_active', true),
      supabase
        .from('todo_checkoffs')
        .select('*')
        .eq('client_id', clientId)
        .order('date', { ascending: false }),
    ]);

    setItems(itemsRes.data || []);
    setCheckoffs(checkoffsRes.data || []);
    setLoading(false);
  }

  if (loading) {
    return <div className="tab-loading">Loading compliance data...</div>;
  }

  const totalItems = items.length;
  const startDate = new Date(client.start_date || client.created_at);
  const now = new Date();
  const daysSinceStart = Math.max(1, Math.floor((now - startDate) / (1000 * 60 * 60 * 24)));

  // Overall tracking rate
  const expectedTotal = totalItems * daysSinceStart;
  const overallRate = expectedTotal > 0
    ? Math.min(100, Math.round((checkoffs.length / expectedTotal) * 100))
    : 0;

  // Per-block breakdown
  const blockStats = TIME_BLOCKS.map((block) => {
    const blockItems = items.filter((i) => i.time_block === block.key);
    const blockItemIds = new Set(blockItems.map((i) => i.id));
    const blockCheckoffs = checkoffs.filter((c) => blockItemIds.has(c.todo_item_id));
    const expected = blockItems.length * daysSinceStart;
    const rate = expected > 0 ? Math.min(100, Math.round((blockCheckoffs.length / expected) * 100)) : 0;
    return { ...block, count: blockItems.length, rate };
  }).filter((b) => b.count > 0);

  // Daily grid (last 14 days)
  const last14Days = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().split('T')[0];
    const dayCheckoffs = checkoffs.filter((c) => c.date === dateStr);
    const dayRate = totalItems > 0
      ? Math.min(100, Math.round((dayCheckoffs.length / totalItems) * 100))
      : 0;
    last14Days.push({
      date: dateStr,
      day: d.toLocaleDateString('en-US', { weekday: 'short' }),
      dayNum: d.getDate(),
      completed: dayCheckoffs.length,
      total: totalItems,
      rate: dayRate,
    });
  }

  function getRateLevel(rate) {
    if (rate >= 75) return 'good';
    if (rate >= 50) return 'moderate';
    if (rate > 0) return 'poor';
    return 'none';
  }

  return (
    <div className="compliance-tab">
      {/* Overall rate */}
      <div className="comp-overall">
        <div className={`comp-overall-ring ring-${getRateLevel(overallRate)}`}>
          <span className="comp-overall-pct">{overallRate}%</span>
        </div>
        <div className="comp-overall-info">
          <h3>Overall Tracking Rate</h3>
          <p>{checkoffs.length} of {expectedTotal} expected check-offs</p>
          <p>{daysSinceStart} day{daysSinceStart !== 1 ? 's' : ''} since start · {totalItems} active item{totalItems !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Daily grid */}
      <div className="comp-section">
        <h4 className="comp-section-title">Last 14 Days</h4>
        <div className="comp-grid">
          {last14Days.map((day) => (
            <div key={day.date} className="comp-grid-cell" title={`${day.date}: ${day.completed}/${day.total}`}>
              <span className="comp-grid-day">{day.day}</span>
              <div className={`comp-grid-dot dot-${getRateLevel(day.rate)}`}>
                {day.dayNum}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Block breakdown */}
      {blockStats.length > 0 && (
        <div className="comp-section">
          <h4 className="comp-section-title">By Time Block</h4>
          <div className="comp-category-list">
            {blockStats.map((block) => (
              <div key={block.key} className="comp-category-row">
                <div className="comp-cat-label">
                  <span>{block.icon}</span>
                  <span>{block.label}</span>
                </div>
                <div className="comp-cat-bar-wrap">
                  <div className="comp-cat-bar">
                    <div
                      className={`comp-cat-fill fill-${getRateLevel(block.rate)}`}
                      style={{ width: `${block.rate}%` }}
                    />
                  </div>
                  <span className="comp-cat-pct">{block.rate}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
